import { useQuery, useSubscription } from "@apollo/client/react";
import { gql } from "@lytra/frontend-common/__generated__";
import type { AgentSyncSubscription } from "@lytra/frontend-common/__generated__/graphql";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { CONFIG } from "@/consts/config";

const AGENT_SYNC_SUBSCRIPTION = gql(`
  subscription AgentSync($agentId: String) {
    agentSync(agentId: $agentId) {
      currentId
      neptuneOnline
      neptunePlatform
    }
  }
`);

// Lookup conversation + message by currentId (external ticket URL / raw ID).
// Neptune only sends currentId; Venus resolves the rest itself.
const CURRENT_ID_LOOKUP_QUERY = gql(`
  query AgentSyncCurrentIdLookup($id: String!) {
    conversationMessageByAnyId(id: $id) {
      id
      conversation {
        id
      }
    }
  }
`);

// Query for initial state on page load — reads last stored sync from Valkey
const AGENT_SYNC_STATE_QUERY = gql(`
  query AgentSyncState($agentId: String) {
    valkeyOnline
    agentSyncState(agentId: $agentId) {
      currentId
      neptuneOnline
      neptunePlatform
    }
  }
`);

// Client-side watchdog: if no beat (subscription event or a successful poll
// showing online) arrives within this window, treat Neptune as gone. Mirrors
// the backend stale cutoff (Neptune heartbeat + grace) and is env-tunable via
// config so the two stay in sync. Default 35s.
const HEARTBEAT_TIMEOUT_MS = CONFIG.agentSyncTimeoutMs ?? 35_000;

export interface AgentSyncState {
  conversationId: string | null;
  messageId: string | null;
  currentId: string | null;
  neptuneOnline: boolean;
  neptunePlatform: string | null;
  valkeyOnline: boolean;
  loading: boolean;
}

export function useAgentSync(options?: { skip?: boolean }): AgentSyncState {
  const skip = options?.skip ?? false;
  const auth = useAuth();
  const agentId = auth.user?.profile?.sub ?? null;
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the heartbeat timeout whenever we receive data
  const resetHeartbeatTimeout = useCallback(() => {
    setTimedOut(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
    }, HEARTBEAT_TIMEOUT_MS);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const onData = useCallback(
    ({ data }: useSubscription.OnDataOptions<AgentSyncSubscription>) => {
      const event = data.data?.agentSync;
      if (event) resetHeartbeatTimeout();
    },
    [resetHeartbeatTimeout],
  );

  // Query for sync state — also polls every 10s as a fallback for missed
  // subscription events (pub/sub is at-most-once, messages can be lost
  // during WS reconnects or brief network blips)
  const { data: queryData, loading: queryLoading } = useQuery(
    AGENT_SYNC_STATE_QUERY,
    {
      variables: { agentId },
      pollInterval: 10_000,
      fetchPolicy: "network-only",
      skip,
    },
  );

  // Apollo Client 4 has no `onCompleted` option on useQuery, so the heartbeat reset
  // has to run off `queryData` directly: each poll delivers a new value, which
  // re-runs this effect.
  // Genuinely reacting to external data (a network poll) to reset an
  // external timer (setTimeout), not deriving from props/state -- exactly
  // the case useEffect exists for.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (queryData?.agentSyncState?.neptuneOnline) {
      resetHeartbeatTimeout();
    }
  }, [queryData, resetHeartbeatTimeout]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Subscribe for live updates scoped to this user's agent
  const { data: subData, loading: subLoading } = useSubscription(
    AGENT_SYNC_SUBSCRIPTION,
    {
      variables: { agentId },
      onData,
      skip,
    },
  );

  // Merge sub + query into a single "last write wins" state. Apollo's
  // useSubscription keeps subData set to the last received event even
  // across WebSocket reconnects, so a dropped event during a brief WS
  // blip would leave subData permanently stale. The fallback order
  // `subData ?? queryData` hid this because subData, once set, always
  // won — the 10s polling against Valkey (source of truth) never got
  // a chance to correct it. Feed both sources into local state so the
  // poll becomes a real reconciliation path: if subData is stale, the
  // next poll overwrites it with Valkey's current value.
  //
  // Render-time updates (not useEffect) so the rebind is synchronous
  // and doesn't flash a stale value for a render. Idempotent because
  // once prevSub/prevQuery match, no further setState runs.
  interface SyncEventShape {
    currentId?: string | null;
    neptuneOnline?: boolean | null;
    neptunePlatform?: string | null;
  }
  // Prefer queryData on first mount if Apollo already has a cached value —
  // otherwise seed from subData if that somehow cached, else null. Without
  // this the prev* refs get initialized alongside subData/queryData on the
  // first render, so if Apollo returns cached data immediately the
  // "changed" check would never fire and syncEvent would stay null.
  const initialSyncEvent: SyncEventShape | null =
    queryData?.agentSyncState ?? subData?.agentSync ?? null;
  const [syncEvent, setSyncEvent] = useState<SyncEventShape | null>(
    initialSyncEvent,
  );
  const [prevSubData, setPrevSubData] = useState(subData);
  const [prevQueryData, setPrevQueryData] = useState(queryData);

  if (subData !== prevSubData) {
    setPrevSubData(subData);
    if (subData?.agentSync) {
      setSyncEvent(subData.agentSync);
    }
  }
  if (queryData !== prevQueryData) {
    setPrevQueryData(queryData);
    // queryData === undefined while the first fetch is in flight — don't
    // overwrite with nothing. Once we have a response, write through
    // even if it's null: null means Valkey has no state (Neptune offline
    // or TTL expired), which should clear any stale subData.
    if (queryData !== undefined) {
      setSyncEvent(queryData.agentSyncState ?? null);
    }
  }

  // If the heartbeat watchdog fired, treat Neptune as offline.
  const neptuneOnline = timedOut ? false : (syncEvent?.neptuneOnline ?? false);

  const liveCurrentId = syncEvent?.currentId ?? null;

  // Keep the last ticket we actually saw, so the portal can keep showing it
  // after the plugin goes offline — only the connection indicator greys out, the
  // content persists (ticket #372). While online we follow the live currentId
  // (including null when the agent is on a no-ticket page); while offline we fall
  // back to the last known ticket.
  const [lastKnownCurrentId, setLastKnownCurrentId] = useState<string | null>(
    null,
  );
  // Adjust state during render instead of an effect: remembers the last seen
  // currentId as soon as it changes, without an extra render pass.
  const [prevLiveCurrentId, setPrevLiveCurrentId] = useState(liveCurrentId);
  if (liveCurrentId !== prevLiveCurrentId) {
    setPrevLiveCurrentId(liveCurrentId);
    if (liveCurrentId) setLastKnownCurrentId(liveCurrentId);
  }

  const currentId = neptuneOnline ? liveCurrentId : lastKnownCurrentId;

  // Neptune only sends currentId. Venus resolves conversation + message itself.
  // Not gated on neptuneOnline, so a disconnected portal keeps rendering the
  // last resolved ticket.
  const { data: lookupData } = useQuery(CURRENT_ID_LOOKUP_QUERY, {
    variables: { id: currentId ?? "" },
    skip: skip || !currentId,
    fetchPolicy: "network-only",
  });

  const lookupResult = lookupData?.conversationMessageByAnyId;
  const conversationId =
    (lookupResult?.conversation?.id as string | null | undefined) ?? null;
  const messageId = (lookupResult?.id as string | null | undefined) ?? null;

  return {
    conversationId,
    messageId,
    currentId,
    neptuneOnline,
    neptunePlatform: syncEvent?.neptunePlatform ?? null,
    valkeyOnline: queryData?.valkeyOnline ?? true,
    loading: queryLoading && !queryData && subLoading,
  };
}
